import React from 'react';
import { CustomDropdown } from '../common/index.js';
import './InsightsView.css';
import '../common/TabPageTitle.css';
import useInsightsQuery from './useInsightsQuery.js';
import useInsightsData from './useInsightsData.js';
import useInsightsTables from './useInsightsTables.js';
import InsightsFilterBar from './InsightsFilterBar.js';
import InsightsModeFilters from './InsightsModeFilters.js';
import InsightsDocumentsSection from './InsightsDocumentsSection.js';
import InsightsTasksSection from './InsightsTasksSection.js';
import InsightsConsultationsSection from './InsightsConsultationsSection.js';
import { useAggregationMode } from './ChartCardControls.js';

const InsightsView = ({
  title = 'Insights',
  subtitle = undefined,
  fetchInsights,
  fetchTasksSnapshots,
  fetchConsultationsSnapshots,
  fetchDocumentReviewInsights,
  fetchDocumentsReviewAggregate,
  fetchConsultationsReviewSummary,
  fetchConsultationsReviewAggregate,
  currentProcessorState = null,
  currentDocumentsAssigned = null,
  currentTasksState = null,
  currentConsultationsState = null,
  hasDocsRead = true,
  hasTaskRead = true,
  hasConsultRead = true,
  clinicSelector = null,
  selectedClinicId = null,
  userIdToName = {},
  activeUsers = [],
  teams = [],
  showUserCharts = true,
  onDocumentClick = null,
  isSidebarExpanded = false,
  titleActions = null
}) => {
  const query = useInsightsQuery({
    teams,
    userIdToName,
    hasDocsRead,
    hasTaskRead,
    hasConsultRead,
  });
  const data = useInsightsData({
    insightsType: query.insightsType,
    viewMode: query.viewMode,
    getDateRange: query.getDateRange,
    dateRangeParams: query.dateRangeParams,
    selectedClinicId,
    fetchInsights,
    fetchTasksSnapshots,
    fetchConsultationsSnapshots,
    fetchDocumentReviewInsights,
    fetchDocumentsReviewAggregate,
    fetchConsultationsReviewSummary,
    fetchConsultationsReviewAggregate,
  });
  const aggregationMode = useAggregationMode();
  const tables = useInsightsTables({
    data: data.data,
    taskData: data.taskData,
    consultationData: data.consultationData,
    reviewData: data.reviewData,
    staffAggregateData: data.staffAggregateData,
    consultationStaffAggregateData: data.consultationStaffAggregateData,
    teams,
    userIdToName,
    insightsType: query.insightsType,
    currentProcessorState,
    currentTasksState,
    currentConsultationsState,
  });

  const sectionProps = {
    ...query,
    ...data,
    ...tables,
    teams,
    userIdToName,
    activeUsers,
    showUserCharts,
    onDocumentClick,
    currentDocumentsAssigned,
    currentProcessorState,
    aggregationMode,
  };

  if (data.loading) {
    return (
      <div className="insights-view">
        <div className="loading-container">
          <p>Loading insights...</p>
        </div>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="insights-view">
        <div className="error-container">
          <p>Error loading insights: {data.error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const showPractice = query.viewMode === 'gp-practice' && tables.hasInsightsData;
  const showStaff = query.viewMode === 'staff';

  return (
    <div className={`insights-view ${isSidebarExpanded ? 'sidebar-expanded' : ''}`}>
      <div className="header">
        <div className="header-title">
          <div className="header-title-text">
            <div className="header-title-with-help">
              <h1 className="tab-page-title">{title}</h1>
              {titleActions}
            </div>
            {query.insightsTypeOptions.length > 0 && (
              <CustomDropdown
                options={query.insightsTypeOptions}
                selectedOption={query.insightsTypeDropdown.selected.display}
                selectedIcon={query.selectedInsightsTypeIcon}
                onSelect={(option) => {
                  query.insightsTypeDropdown.handleSelect(option);
                  query.handleInsightsTypeChange(option);
                }}
                isOpen={query.insightsTypeDropdown.isOpen}
                toggleDropdown={() => query.insightsTypeDropdown.toggle([])}
                keyExtractor={(option, index) => `insights-type-${option.value || index}`}
              />
            )}
          </div>
          {clinicSelector}
        </div>
        <InsightsFilterBar {...sectionProps} />
      </div>

      {query.viewMode === 'gp-practice' && (
        tables.hasInsightsData ? (
          <InsightsModeFilters {...sectionProps} />
        ) : (
          <div className="no-data-container">
            <p>
              {query.insightsType === 'documents'
                ? 'No insights data available'
                : query.insightsType === 'consultations'
                  ? 'No scribe insights data available'
                  : 'No task insights data available'}
            </p>
          </div>
        )
      )}

      {(showPractice || showStaff) && query.insightsType === 'documents' && (
        <InsightsDocumentsSection {...sectionProps} />
      )}
      {(showPractice || showStaff) && query.insightsType === 'tasks' && (
        <InsightsTasksSection {...sectionProps} />
      )}
      {(showPractice || showStaff) && query.insightsType === 'consultations' && (
        <InsightsConsultationsSection {...sectionProps} />
      )}
    </div>
  );
};

export default React.memo(InsightsView);
